import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, apiMultipart } from '../lib/api.ts';
import { Aviso, Boton, BotonArchivo, Campo, EncabezadoPagina, Selector, Tarjeta } from '../componentes/ui.tsx';

interface Logo {
  id: string;
  nombre: string;
  url: string;
}
interface Contacto {
  id: string;
  canal: string;
  valor: string;
  principal: boolean;
}

export function PaginaMiCuenta() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [canal, setCanal] = useState('WHATSAPP');
  const [valor, setValor] = useState('');
  const [error, setError] = useState<string | null>(null);

  const logos = useQuery({
    queryKey: ['perfil-logos'],
    queryFn: () => api<{ items: Logo[] }>('/perfil/logos'),
  });
  const contactos = useQuery({
    queryKey: ['perfil-contactos'],
    queryFn: () => api<{ items: Contacto[] }>('/perfil/contactos'),
  });

  const subirLogo = useMutation({
    mutationFn: async () => {
      if (!archivo) throw new Error(t('cuenta.eligeArchivo'));
      const forma = new FormData();
      forma.append('archivo', archivo);
      await apiMultipart('/perfil/logos', forma);
    },
    onSuccess: async () => {
      setArchivo(null);
      setError(null);
      await qc.invalidateQueries({ queryKey: ['perfil-logos'] });
    },
    onError: (e) => setError(e.message),
  });

  const borrarLogo = useMutation({
    mutationFn: (id: string) => api(`/perfil/logos/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perfil-logos'] }),
  });

  const crearContacto = useMutation({
    mutationFn: () =>
      api('/perfil/contactos', { metodo: 'POST', cuerpo: { canal, valor: valor.trim() } }),
    onSuccess: async () => {
      setValor('');
      await qc.invalidateQueries({ queryKey: ['perfil-contactos'] });
    },
    onError: (e) => setError(e.message),
  });

  const borrarContacto = useMutation({
    mutationFn: (id: string) => api(`/perfil/contactos/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['perfil-contactos'] }),
  });

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina titulo={t('cuenta.titulo')} />
      {error ? <Aviso>{error}</Aviso> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Tarjeta titulo={t('cuenta.misLogos')}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <BotonArchivo etiqueta="PNG / JPG" accept="image/*" onArchivo={setArchivo} nombre={archivo?.name ?? null} />
          <Boton onClick={() => subirLogo.mutate()} disabled={subirLogo.isPending || !archivo}>
            {t('cuenta.subir')}
          </Boton>
        </div>
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {logos.data?.items.map((l) => (
            <li key={l.id} className="interno p-2">
              <img src={l.url} alt={l.nombre} className="aspect-square w-full object-contain" />
              <Boton variante="texto" tamano="sm" className="w-full" onClick={() => borrarLogo.mutate(l.id)}>
                {t('comun.borrar')}
              </Boton>
            </li>
          ))}
        </ul>
      </Tarjeta>

      <Tarjeta titulo={t('cuenta.contactos')}>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            crearContacto.mutate();
          }}
        >
          <Selector etiqueta={t('cuenta.canal')} value={canal} onChange={(e) => setCanal(e.target.value)}>
            <option value="WHATSAPP">WhatsApp</option>
            <option value="INSTAGRAM">Instagram</option>
            <option value="FACEBOOK">Facebook</option>
            <option value="TIKTOK">TikTok</option>
          </Selector>
          <div className="flex-1">
            <Campo
              etiqueta={t('cuenta.valor')}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="+593 99 123 4567 / @minegocio"
              required
            />
          </div>
          <Boton type="submit" disabled={crearContacto.isPending}>
            {t('comun.agregar')}
          </Boton>
        </form>
        <ul className="mt-4 divide-y divide-superficie2">
          {contactos.data?.items.map((c) => (
            <li key={c.id} className="flex items-center justify-between py-2.5 text-sm">
              <span>
                <span className="tag mr-2">{c.canal}</span>{c.valor}
              </span>
              <Boton variante="texto" tamano="sm" onClick={() => borrarContacto.mutate(c.id)}>
                {t('comun.borrar')}
              </Boton>
            </li>
          ))}
          {contactos.data?.items.length === 0 ? (
            <li className="py-4 text-sm text-suave">{t('cuenta.sinContactos')}</li>
          ) : null}
        </ul>
      </Tarjeta>
      </div>
    </main>
  );
}
